/**
 * Portless-blocked orders at a floor (BLU-713 / BLU-732).
 *
 * One shared read for every surface that needs the blocked set or its count:
 * the Issues → Shipping tab list, the Shipping tab badge, the sidebar nav
 * badge, and the Dashboard alert total. All callers share one react-query
 * cache entry per location, so the badge and the list can never disagree.
 *
 * Polling matches useShippingFailures exactly — the two are summed into one
 * badge, and these episodes self-resolve via a background 10-minute sweep, so
 * a non-polling half would freeze the badge on an always-focused floor screen.
 * The staleTime also gives sub-tab remounts a dedupe window instead of
 * refiring the endpoint on every toggle.
 *
 * Endpoint authz: protectedProcedure + Permission.OrdersView (same as
 * getShippingFailures) — safe for Viewer and for pure-client orgs at a 3PL
 * floor. Pass `null` to skip (AppSidebar skips for station users, matching
 * its other coordinator queries).
 */
import { skipToken } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';

export function usePortlessBlocked(locationId: string | null) {
  return trpc.shipping.listPortlessBlocked.useQuery(locationId ? { locationId } : skipToken, {
    refetchInterval: 30 * 1000, // 30 seconds — matches useShippingFailures + issue counts
    staleTime: 15 * 1000,
  });
}

/** Row count only — what the tab / nav badges need. Loading or skipped → 0. */
export function usePortlessBlockedCount(locationId: string | null): number {
  const query = usePortlessBlocked(locationId);
  return query.data?.length ?? 0;
}
